import { useCallback, useEffect } from 'react';

import { ICardData, IVitrineData } from './types';
import { eventPushLogger, formatEventData, processLoopCards } from './utils';

export function useVitrineTagging(vitrineData: IVitrineData) {
  const { events_tag } = vitrineData;

  const pushToDataLayer = useCallback((eventData: any) => {
    eventPushLogger(eventData);

    window.dataLayer = Array.isArray(window.dataLayer) ? window.dataLayer : [];
    window.dataLayer.push(eventData);
  }, []);

  useEffect(() => {
    if (events_tag) {
      const loadEvents = events_tag.events.filter(event =>
        event.triggers.some(
          trigger => trigger === 'rec:loaded' || trigger === 'details:loaded',
        ),
      );

      loadEvents.forEach(eventConfig => {
        let eventPayloadData = { ...events_tag.data };
        if (eventConfig.event === 'view_item_list' && eventConfig.loopCards) {
          const loopedData = processLoopCards(eventConfig, vitrineData);

          eventPayloadData = {
            ...eventPayloadData,
            ...loopedData,
            ecommerce: {
              ...(eventPayloadData.ecommerce || {}),
              items: loopedData?.ecommerce?.items || [],
            },
          };

          const modifiedTemplate = {
            ...eventConfig.template,
            ecommerce: {
              ...(eventConfig.template.ecommerce || {}),
              items: loopedData?.ecommerce?.items || [],
            },
          };

          const eventData = formatEventData(modifiedTemplate, {
            data: eventPayloadData,
            vitrine: vitrineData,
          });

          if (eventData) {
            pushToDataLayer(eventData);
          }
        } else {
          const eventData = formatEventData(eventConfig.template, {
            data: eventPayloadData,
            vitrine: vitrineData,
          });

          if (eventData) {
            pushToDataLayer(eventData);
          }
        }
      });
    }
  }, [events_tag, vitrineData, pushToDataLayer]);

  const triggerCardClick = useCallback(
    (card: ICardData) => {
      if (events_tag) {
        const selectItemEventConfig = events_tag.events.find(
          event =>
            event.event === 'select_item' &&
            event.triggers.includes('card:clicked'),
        );

        if (selectItemEventConfig) {
          const eventData = formatEventData(selectItemEventConfig.template, {
            current: { data: card },
            data: events_tag.data,
            vitrine: vitrineData,
          });

          if (eventData) {
            pushToDataLayer(eventData);
          }
        }
      }
    },
    [events_tag, vitrineData, pushToDataLayer],
  );

  const triggerSelectContentClick = useCallback(
    (elementInfo: { label?: string; title?: string; [key: string]: any }) => {
      if (events_tag) {
        const selectContentEventConfig = events_tag.events.find(
          event =>
            event.event === 'select_content' &&
            event.triggers.some(trigger => trigger.includes(':click')),
        );

        if (selectContentEventConfig) {
          const eventData = formatEventData(selectContentEventConfig.template, {
            current: elementInfo,
            data: events_tag.data,
            vitrine: vitrineData,
          });

          if (eventData) {
            pushToDataLayer(eventData);
          }
        }
      }
    },
    [events_tag, vitrineData, pushToDataLayer],
  );

  return {
    triggerCardClick,
    triggerSelectContentClick,
  };
}
